/**
 * Hiring-demand board — the frame around the rows: column header, the roll-up
 * row every group level shares, the summary tiles and the per-department footer.
 *
 * Split out of HiringDemandTab so that file stays about state rather than markup
 * (and to keep both under the repo's 500-line ceiling).
 */
import { AlertCircle, Info, RefreshCw, Loader2 } from 'lucide-react';
import type { BoardRole, DemandMonth } from '../../../types/taDemand';
import {
  fteOf,
  heads,
  tally,
  type DeptNode,
  type Track,
} from './demandModel';
import { Caret, DeltaBadge, fmtFte, relativeTime, TallyPill, zoneClass } from './demandUi';
import { indentPx } from './demandGrid';

/**
 * Column widths.
 *
 * NAME_W is generous on purpose (Theresa, Sep 2026: "every column can take their
 * space since we now introduced the scrolling option"). At 460 the frozen column
 * squeezed its sub-columns into itself and truncated real names to "Lola H…",
 * which is the opposite of what a team list is for. It has to hold six tracks
 * now — one of them wide enough for a long German name, because a backfill's
 * "whose seat" and a colleague's own name both land there. The board scrolls
 * sideways either way, being fifteen months wide, so buying legibility here
 * costs nothing that was not already being paid.
 */
export const NAME_W = 740;
export const MONTH_W = 54;
export const GROUP_W = 96;
/**
 * THERE IS NO TRAILING COLUMN ANY MORE.
 *
 * It ended up carrying only the group roll-ups — blank on every role, person and
 * draft row — so 120 pixels of the board existed for six rows in it. The total
 * moved into the group row's own frozen cell, which had empty space at exactly
 * that end, and the months got the width back. Kept as a named zero so the
 * arithmetic in useDemandLayout still reads as "name, months, and what follows".
 */
export const END_W = 0;

/** Department accents, matched to the Check-In tab so the cockpit reads as one. */
export const DEPT_DOT: Record<string, string> = {
  Growth: '#b91c1c',
  Operations: '#92400e',
  Fleet: '#1e40af',
  'Finance & Legal': '#166534',
  Tech: '#7e22ce',
  People: '#0e7490',
};

/**
 * The first column is FROZEN. The board is 15 months wide plus a trailing
 * column, so the name scrolls out of view long before the far months arrive —
 * and a row of numbers with no name attached is unreadable. Every cell that
 * belongs to it carries this, and each sets its own background because a
 * sticky cell paints over whatever slides beneath it.
 */
export const stickyName = 'sticky left-0 z-[2]';

export const Rail = () => (
  <span className="ml-3.5 w-3 shrink-0 self-stretch border-r border-gray-100" />
);

/**
 * A department / unit / team row.
 *
 * It carries its own per-month roll-up, which is the point of the whole layout:
 * collapsing a unit compresses its demand rather than hiding it, so a lead can
 * still read the shape of twelve months at a glance.
 */
export function GroupRow({
  tracks,
  months,
  currentMonth,
  depth,
  open,
  onClick,
  tone,
  roles,
  label,
  actions,
  drop,
}: {
  tracks: Track[];
  months: DemandMonth[];
  currentMonth: string;
  depth: number;
  open: boolean;
  onClick: () => void;
  tone: 'dept' | 'unit' | 'team';
  roles: BoardRole[];
  label: React.ReactNode;
  /** Controls that belong to this group — submit, add a team. */
  actions?: React.ReactNode;
  /**
   * Makes this header a drop target for a role being dragged into its team.
   * `active` is the highlight while something acceptable is over it — see
   * TeamMove.tsx for why the drag is tracked in state rather than read off the
   * dataTransfer.
   */
  drop?: { active: boolean; handlers: React.HTMLAttributes<HTMLElement> };
}) {
  const cells = tally(roles, tracks, months, currentMonth);
  const bg =
    tone === 'dept'
      ? 'bg-gray-50 border-gray-200 min-h-[52px]'
      : 'border-gray-100 min-h-[38px] hover:bg-gray-50';
  return (
    <div
      {...drop?.handlers}
      className={`group grid items-stretch border-b ${bg} ${
        drop?.active ? 'outline outline-2 -outline-offset-2 outline-blue-500 bg-blue-100' : ''
      }`}
      style={{ gridTemplateColumns: 'var(--demand-tmpl)' }}
    >
      {/*
        ⚠️ The ROW is not the button — a real `<button>` around the caret and the
        label is, with the unit's controls as its SIBLING. The row used to carry
        `role="button"`, which put `Add team` and `Submit unit` inside a button:
        `stopPropagation` kept them from toggling the row, but nested interactive
        elements are invalid either way and a screen reader loses what the inner
        controls are. Clicking a month cell no longer collapses the group, which
        is the cost, and a small one — those cells are a roll-up to read.

        A group row does NOT use the five-column sub-grid the rows below it
        share: it has no level, no reason and no status, and its controls need
        real room — "Add team" and "Submit unit" together are wider than the
        104px an action cell gets. So the label runs the width of the frozen
        column, using the same indent function so the carets still line up with
        the rows they open.
      */}
      <div
        className={`sticky left-0 z-[2] flex min-w-0 items-center gap-2 border-r border-gray-200 pr-2 ${
          depth === 0 ? 'bg-gray-50' : 'bg-white'
        } ${tone !== 'dept' ? 'group-hover:bg-gray-50' : ''}`}
        style={{ paddingLeft: indentPx(depth) }}
      >
        {/*
          The controls sit BESIDE the unit name rather than pushed to the far
          right of an 800-pixel column: the button and its label belong to each
          other, and a gap that wide reads as two unrelated things. `flex-1` is
          on the spacer after them, not on the label.
        */}
        <button
          type="button"
          aria-expanded={open}
          onClick={onClick}
          className="flex min-w-0 items-center gap-1.5 rounded-sm py-1 text-left"
        >
          <Caret open={open} />
          {label}
        </button>
        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
        <span className="min-w-0 flex-1" />
        {/*
          THE ROLL-UP, at the right end of the row it belongs to. It used to have
          a column of its own past the last month — 120 pixels that were BLANK on
          every role, person and draft row, purely so that six group rows had
          somewhere to put a total. The group row's own frozen cell had empty
          space at exactly that end, so the total moved into it and the months
          got the column back.
        */}
        <span className="shrink-0 whitespace-nowrap text-[13px] font-semibold tabular-nums text-gray-900">
          {heads(roles)}
          <span className="ml-1 text-[11px] font-medium text-gray-400">
            · {fmtFte(fteOf(roles))} FTE
          </span>
        </span>
      </div>
      {tracks.map((t, i) => (
        <div
          key={i}
          className={`flex flex-col items-center justify-center ${zoneClass(t)}`}
        >
          <TallyPill cell={cells[i]} />
          <DeltaBadge delta={cells[i].delta} />
        </div>
      ))}
    </div>
  );
}

/**
 * The lookahead band is NAMED after the quarter it covers ("Q1 2027 outlook"),
 * not called "Lookahead". It is the reason this collection round exists — a
 * January start is a September search — and a generic label made the most
 * important columns on the board read as an afterthought.
 */
function bandText(band: string, tracks: Track[]): string {
  if (band === 'past') return 'Year to date · actuals';
  if (band === 'plan') {
    const y = tracks.flatMap((t) => (t.kind === 'month' ? [t.month] : [])).find((m) => m.band === 'plan');
    return `Rest of ${y?.year ?? ''} · in the HDB`;
  }
  const ms = tracks.flatMap((t) => (t.kind === 'month' ? [t.month] : [])).filter((m) => m.band === 'lookahead');
  if (!ms.length) return 'Outlook · your estimate';
  const q = Math.floor((Number(ms[0].key.slice(5, 7)) - 1) / 3) + 1;
  return `Q${q} ${ms[0].year} outlook · your estimate`;
}
const BAND_CLS: Record<string, string> = {
  past: 'border-gray-300 text-gray-400',
  plan: 'border-gray-900 text-gray-900',
  lookahead: 'border-amber-500 text-amber-600',
};

export function HeaderRow({
  tracks,
  showPast,
  onTogglePast,
  pastHires,
}: {
  tracks: Track[];
  showPast: boolean;
  onTogglePast: () => void;
  pastHires: number;
}) {
  const bands: { band: string; start: number; span: number }[] = [];
  tracks.forEach((t, i) => {
    const b = t.kind === 'group' ? 'past' : t.month.band;
    const last = bands[bands.length - 1];
    if (last && last.band === b) last.span++;
    else bands.push({ band: b, start: i + 2, span: 1 });
  });

  return (
    <div
      className="sticky top-0 z-10 grid items-stretch border-b border-gray-200 bg-gray-50"
      style={{ gridTemplateColumns: 'var(--demand-tmpl)', gridTemplateRows: 'auto auto' }}
    >
      <div
        className={`${stickyName} flex items-end justify-between gap-2 border-r border-gray-200 bg-gray-50 px-3.5 pb-2`}
        style={{ gridRow: '1 / span 2' }}
      >
        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Department › Unit › Team
        </span>
        <button
          type="button"
          onClick={onTogglePast}
          className="shrink-0 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[11px] font-semibold text-blue-700 hover:bg-blue-50"
        >
          {showPast ? 'Collapse past months' : 'Show past months'}
        </button>
      </div>
      {bands.map((b) => (
        <div
          key={b.band + b.start}
          className={`mx-1 flex items-center justify-center border-b-2 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wider ${BAND_CLS[b.band]}`}
          style={{ gridRow: 1, gridColumn: `${b.start} / span ${b.span}` }}
        >
          {bandText(b.band, tracks)}
        </div>
      ))}
      {tracks.map((t, i) =>
        t.kind === 'group' ? (
          <button
            key={i}
            type="button"
            onClick={onTogglePast}
            title="Expand the past months"
            className="flex flex-col items-center justify-center gap-0.5 bg-gray-50 pb-1.5 pt-1 hover:bg-gray-100"
            style={{ gridRow: 2 }}
          >
            <b className="text-[11px] font-semibold text-gray-600">{t.label}</b>
            <span className="text-[10px] text-gray-400">▸ {pastHires} hires</span>
          </button>
        ) : (
          <div
            key={i}
            className={`flex flex-col items-center justify-center gap-px pb-2 pt-1.5 text-[11px] font-medium ${
              t.month.past
                ? 'bg-gray-50 text-gray-400'
                : t.month.band === 'lookahead'
                  ? 'bg-amber-50 text-amber-700'
                  : 'text-gray-500'
            }`}
            style={{ gridRow: 2 }}
          >
            <b className="text-xs font-semibold text-gray-900">{t.month.label}</b>
            {String(t.month.year).slice(2)}
          </div>
        ),
      )}
    </div>
  );
}

export function SectionChip({
  section,
  unitsSubmitted,
  unitsTotal,
}: {
  section?: { submittedAt?: string; lastEditAt?: string };
  unitsSubmitted: number;
  unitsTotal: number;
}) {
  // The chip reports UNIT progress, not merely whether somebody typed. It used
  // to read "submitted" as soon as the department was signed off — so one test
  // role on one unit made the whole of Growth look finished.
  const done = section?.submittedAt && unitsSubmitted === unitsTotal;
  const cls = done
    ? 'bg-emerald-50 text-emerald-700'
    : unitsSubmitted > 0 || section?.lastEditAt
      ? 'bg-amber-50 text-amber-700'
      : 'bg-gray-100 text-gray-400';
  const label = done
    ? 'complete'
    : section?.submittedAt
      ? `signed off · ${unitsSubmitted}/${unitsTotal} units`
      : unitsSubmitted > 0
        ? `${unitsSubmitted}/${unitsTotal} units in`
        : section?.lastEditAt
          ? 'in progress'
          : 'not started';
  return (
    <span className={`shrink-0 rounded-sm px-1.5 text-[11px] font-semibold leading-4 ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Submitting records who and when but does not lock anything — Theresa's call.
 * The button therefore says "still editable" rather than implying a freeze.
 */
export function DeptFooter({
  dept,
  section,
  unitsSubmitted,
  unitsTotal,
  busy,
  onSubmit,
}: {
  dept: DeptNode;
  unitsSubmitted: number;
  unitsTotal: number;
  section?: {
    submittedBy?: string;
    submittedAt?: string;
    lastEditBy?: string;
    lastEditAt?: string;
  };
  busy: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
      <span>
        Department owner <b className="font-semibold text-gray-900">{dept.owner ?? 'not set'}</b>
        {section?.submittedAt ? (
          <>
            {' '}
            · submitted by <b className="font-semibold text-gray-900">{section.submittedBy}</b>{' '}
            {relativeTime(section.submittedAt)} — still editable
          </>
        ) : section?.lastEditAt ? (
          <>
            {' '}
            · last change by <b className="font-semibold text-gray-900">{section.lastEditBy}</b>{' '}
            {relativeTime(section.lastEditAt)}
          </>
        ) : (
          ' · no input yet'
        )}
      </span>
      <span className="flex items-center gap-3">
        <span className="tabular-nums">
          {unitsSubmitted} of {unitsTotal} units submitted
        </span>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          title="The department owner's final sign-off, on top of the unit submissions. Nothing is locked either way."
          className="inline-flex h-7 items-center gap-1.5 rounded-sm bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          {section?.submittedAt ? 'Re-confirm department' : `Sign off ${dept.name}`}
        </button>
      </span>
    </div>
  );
}

export function Banner({
  tone,
  children,
}: {
  tone: 'warn' | 'info';
  children: React.ReactNode;
}) {
  const cls =
    tone === 'warn'
      ? 'border-amber-200 bg-amber-50 text-amber-900'
      : 'border-blue-200 bg-blue-50 text-blue-900';
  const Icon = tone === 'warn' ? AlertCircle : Info;
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${cls}`}>
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export function Legend({ canApprove }: { canApprove: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
      <span className="font-semibold text-gray-900">From the HDB</span>
      <span className="flex items-center gap-1.5">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-emerald-500 text-[10px] font-bold text-white">
          ✓
        </span>
        hired
      </span>
      <span className="flex items-center gap-1.5">
        <span className="h-5 w-5 rounded-full bg-blue-600" />
        open
      </span>
      <span
        className="flex items-center gap-1.5"
        title="Approved but paused. Gets a new start date when recruiting resumes, so it is not counted as upcoming work and is never marked overdue."
      >
        <span className="h-5 w-5 rounded-full border-2 border-amber-500" />
        on hold <span className="text-gray-400">· not counted</span>
      </span>
      <span
        className="flex items-center gap-1.5"
        title="Approved and dated, not opened yet because the start is far enough out. Counts as upcoming work."
      >
        <span className="h-5 w-5 rounded-full border-2 border-dashed border-gray-300" />
        backlog
      </span>
      <span className="h-4 w-px bg-gray-200" />
      <span className="font-semibold text-blue-700">Entered here</span>
      <span className="flex items-center gap-1.5">
        <span className="h-5 w-5 rounded-full border-2 border-dashed border-blue-500" />
        draft
      </span>
      <span className="ml-auto flex items-center gap-1.5">
        <RefreshCw className="h-3 w-3" />
        {canApprove
          ? 'Approve writes the role into the HDB'
          : 'Approving needs the HDB write token — drafts stay here for now'}
      </span>
    </div>
  );
}
